import { Button, Spinner } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { BsArrowRight } from 'react-icons/bs';
import Container from '../shared/Container';
import { text } from 'stream/consumers';
import { useStartMeetingMutation } from 'src/hooks/utils/useStartMeetingMutation.hooks';
import { useParams } from 'react-router-dom';
import { useGetHiringAssessment, useGuestUserValidateJwt } from 'src/hooks/hiringAssessment.hooks';
import { MeetingType, UIHiringAssessment } from 'types';
import { useGetAllMockRoundTemplatesQuery, useGetUserPreferenceMeetingsQuery } from 'src/hooks/meeting.hooks';
import BrowserWarningModal from '../scheduleMeetingDrawer/BrowserWarningModal';
import { useUserContext } from 'src/context/UserProvider';
import { browserName, isMobile } from 'react-device-detect';
import TakePhotoModal from './TakePhotoModal';

interface WelcomeAssessmentProps {
	hiringAssessmentId?: string;
}

const WelcomeAssessment: React.FC<WelcomeAssessmentProps> = ({ hiringAssessmentId }: WelcomeAssessmentProps) => {
	const [openPhotoModal, setOpenPhotoModal] = useState(false);
	const { openBrowserWarningModal, setOpenBrowserWarningModal } = useUserContext();
	const { data: preferenceMeetingData, isLoading: preferenceMeetingDataLoading } = useGetUserPreferenceMeetingsQuery({
		hiringAssessmentId: hiringAssessmentId,
		token: localStorage.getItem('guestUserJwtToken') || '',
	});
	const { data: mockRoundTemplatesData, isLoading: mockRoundTemplatesLoading } = useGetAllMockRoundTemplatesQuery({
		hiringAssessmentId: hiringAssessmentId,
		token: localStorage.getItem('guestUserJwtToken') || '',
	});
	const { data: guestUserValidateJwtData, isLoading: guestUserValidateJwtLoading } = useGuestUserValidateJwt(
		localStorage.getItem('guestUserJwtToken') || '',
	);

	const [finalMockRoundTemplate, setFinalMockRoundTemplate] = useState<string[]>([]);
	const [activeMeetings, setActiveMeetings] = useState<any[]>([]);
	const [hiringAssessment, setHiringAssessment] = useState<UIHiringAssessment>();
	const [finalTemplates, setFinalTemplates] = useState<string[]>([]);

	useEffect(() => {
		if (mockRoundTemplatesData?.mockRoundTemplates && mockRoundTemplatesData?.mockRoundTemplates?.length >= 2) {
			const interviewMockRoundTemplate = mockRoundTemplatesData?.mockRoundTemplates.find(
				template => template.templateType === MeetingType.Interview,
			);
			const assessmentMockRoundTemplate = mockRoundTemplatesData?.mockRoundTemplates.find(
				template => template.templateType === MeetingType.Assessment,
			);
			if (interviewMockRoundTemplate && assessmentMockRoundTemplate) {
				setFinalMockRoundTemplate([
					interviewMockRoundTemplate?._id?.toString(),
					assessmentMockRoundTemplate?._id?.toString(),
				]);
			}
		}
	}, [mockRoundTemplatesData?.mockRoundTemplates]);

	useEffect(() => {
		if (preferenceMeetingData) {
			setActiveMeetings(preferenceMeetingData.activeMeetings || []);
		}
	}, [preferenceMeetingData]);

	const { data: hiringAssessmentData, isLoading: hiringAssessmentLoading } = useGetHiringAssessment({
		hiringAssessmentId: hiringAssessmentId || '',
		token: localStorage.getItem('guestUserJwtToken') || '',
	});

	useEffect(() => {
		if (hiringAssessment?.templates && hiringAssessment?.templates?.length === 2) {
			if (process.env.REACT_APP_NODE_ENV !== 'production') {
				setFinalTemplates(['66c61c4f9d4dc23410241fb3', '66c61da59d4dc2341024c14f']);
			} else {
				setFinalTemplates(hiringAssessment.templates);
			}
		}
	}, [hiringAssessment?.templates]);

	useEffect(() => {
		if (hiringAssessmentData) {
			setHiringAssessment(hiringAssessmentData?.hiringAssessment);
		}
	}, [hiringAssessmentData]);

	// useEffect(() => {
	// 	if (hiringAssessment) {
	// 		console.log(hiringAssessment);
	// 	}
	// }, [hiringAssessment]);

	const startMeetingForRoundTemplateMutation = useStartMeetingMutation();
	const pointsToRemember = [
		{
			image: '/assets/assessmentcompleted/BellSimpleSlash.svg',
			text: 'Ensure you are in a quiet, distraction-free area.',
		},
		{
			image: '/assets/assessmentcompleted/WifiHigh.svg',
			text: 'Make sure your device is charged and with stable internet connection.',
		},
		{
			image: '/assets/assessmentcompleted/Timer.svg',
			text: 'The assessment is timed and will run for 90 minutes',
		},
		{
			image: '/assets/assessmentcompleted/GoogleChromeLogo.svg',
			text: 'Take the assessment only on updated chrome browser',
		},
	];

	const permissions = [
		{
			image: '/assets/assessmentcompleted/Camera.svg',
			text: 'Camera',
		},
		{
			image: '/assets/assessmentcompleted/Headphones.svg',
			text: 'Audio',
		},
		{
			image: '/assets/assessmentcompleted/CornersOut.svg',
			text: 'Entire screen sharing',
		},
		{
			image: '/assets/assessmentcompleted/Microphone.svg',
			text: 'Microphone',
		},
	];

	return (
		<Container className="flex items-center w-full">
			<div className="w-full flex rounded-[8px] bg-[#FFF] shadow-[0px_2px_4px_0px_rgba(0,0,0,0.12)] my-4 flex-col items-center justify-center">
				<h4 className="text-[#28293D] text-center font-inter text-2xl md:text-3xl font-[700] leading-[40px] pb-[10px] md:pb-[30px] pt-[27px] px-2 md:px-0">
					Welcome to your assessment test!
				</h4>
				<p className="inline-block justify-center text-[#555770] text-center font-inter text-[14px] font-[400] leading-[20px] pb-8 md:pb-[57px] max-w-[967.521px] mx-auto px-4 md:px-0">
					Completing this assessment allows you to showcase your skills, ensuring a fair evaluation based on abilities
					rather than resumes, and offering equal opportunities to all candidates. Please go through the instructions
					before you start.
				</p>
				<div className="px-4">
					<p className="text-[#555770] text-center font-inter text-xl md:text-2xl font-[700] pb-[20px] md:pb-[39px] px-2 md:px-0">
						Points to remember before you start
					</p>
					<div className="flex flex-wrap justify-center max-w-[1109px] mx-auto gap-[30px] pb-[35px]">
						{pointsToRemember.map((point, index) => (
							<div
								key={index}
								className="w-full flex justify-center md:justify-start md:max-w-[210px] gap-[16px] items-center"
							>
								<img src={point.image} alt="point" />
								<p className="text-[#555770] font-inter text-[14px] font-[400] leading-[20px] gap-[16px]">
									{point.text}
								</p>
							</div>
						))}
					</div>
				</div>
				<div>
					<p className="text-[#555770] text-center font-inter text-xl md:text-2xl font-[700] pb-[20px] md:pb-[33px] px-2 md:px-0">
						Permissions to be granted
					</p>
					<p className="text-[#555770] text-center text-[14px] inline-block font-[400] leading-[20px] max-w-[914px] mx-auto pb-[20px] md:pb-[37px] px-4 md:px-0">
						For effective assessment, please enable these permissions on your laptop. Snapshots of your screen and your
						answers will be taken of periodically during the assessment. These measures are taken to ensure a fair
						evaluation for everyone.
					</p>
					<div className="max-w-[854px] flex flex-wrap justify-between mx-auto gap-[20px] px-4 md:px-0">
						{permissions.map((permission, index) => (
							<div key={index} className="flex w-full justify-center md:justify-start max-w-[70%] mx-auto md:max-w-max">
								<div className="flex items-center justify-start w-full mx-auto">
									<div className="pr-[16px]">
										<img src={permission.image} alt="permission" />
									</div>
									<p className="font-inter text-[14px] font-[400] leading-[20px] content-center gap-[16px]">
										{permission.text}
									</p>
								</div>
							</div>
						))}
					</div>
				</div>
				<div className="flex justify-center gap-[24px] px-4 py-6 md:pt-[50px]">
					{finalMockRoundTemplate.length === 2 ? (
						<>
							{mockRoundTemplatesData?.totalMockActiveOrCompleted &&
							mockRoundTemplatesData?.totalMockActiveOrCompleted > 0 ? null : (
								<Button
									isProcessing={
										guestUserValidateJwtLoading ||
										startMeetingForRoundTemplateMutation.isLoading ||
										hiringAssessmentLoading
									}
									onClick={() => {
										if (browserName !== 'Chrome' || isMobile) {
											setOpenBrowserWarningModal(true);
											return;
										}

										startMeetingForRoundTemplateMutation.mutate({
											roundTemplateId: finalMockRoundTemplate[0],
											isMock: true,
											hiringAssessmentId: hiringAssessmentId || '',
											token: localStorage.getItem('guestUserJwtToken') || '',
										});
									}}
									disabled={
										guestUserValidateJwtLoading ||
										startMeetingForRoundTemplateMutation.isLoading ||
										!hiringAssessment ||
										hiringAssessmentLoading
									}
									className="text-[#7777E7] font-inter text-[14px] font-medium leading-[150%] bg-[#FFF] border border-solid border-[#7777E7] rounded-[8px]  hover:text-[#FFF] hover:!bg-[#7777E7]	"
								>
									{mockRoundTemplatesData?.activeMockMeetings && mockRoundTemplatesData?.activeMockMeetings?.length > 0
										? 'Continue Mock'
										: 'Practice Mock'}
								</Button>
							)}
						</>
					) : null}
					<Button
						isProcessing={
							guestUserValidateJwtLoading ||
							startMeetingForRoundTemplateMutation.isLoading ||
							!hiringAssessment ||
							hiringAssessmentLoading
						}
						disabled={
							guestUserValidateJwtLoading ||
							startMeetingForRoundTemplateMutation.isLoading ||
							!hiringAssessment ||
							hiringAssessmentLoading
						}
						className={'flex justify-center items-center gap-2 rounded-[8px] text-white bg-[#7777E7]'}
						color="purplePlaced"
						onClick={() => {
							if (browserName !== 'Chrome' || isMobile) {
								setOpenBrowserWarningModal(true);
								return;
							}

							setOpenPhotoModal(true);
						}}
					>
						{activeMeetings.length > 0 ? 'Continue Assessment' : 'Start Assessment'}
					</Button>
				</div>
			</div>

			<BrowserWarningModal
				openModal={openBrowserWarningModal}
				setOpenModal={() => {
					setOpenBrowserWarningModal(false);
				}}
				isJoinedFromMobile={isMobile}
			/>
			<TakePhotoModal
				openModal={openPhotoModal}
				setOpenModal={setOpenPhotoModal}
				userId={guestUserValidateJwtData?.user?._id || ''}
				onFinish={() => {
					setOpenPhotoModal(false);
					startMeetingForRoundTemplateMutation.mutate({
						roundTemplateId: finalTemplates[0],
						isMock: false,
						hiringAssessmentId: hiringAssessmentId || '',
						token: localStorage.getItem('guestUserJwtToken') || '',
					});
				}}
			/>
		</Container>
	);
};

export default WelcomeAssessment;
